import { contentfulClient } from 'connectors/contentful';
import { getEntryId, isEntryHyperlink } from 'connectors/utils/resolve-rich-text-node';
import { Entry } from 'contentful';
import get from 'lodash/get';
import isObject from 'lodash/isObject';
import omit from 'lodash/omit';

const BATCH_SIZE = 100;
const MAX_NESTING_DEPTH = 3;

const findVariantLists = (node: unknown): unknown[][] => {
  const lists: unknown[][] = [];
  const seen = new WeakSet<object>();
  const visit = (value: unknown): void => {
    if (!isObject(value) || seen.has(value) || isEntryHyperlink(value)) {
      return;
    }
    seen.add(value);

    const variants: unknown = get(value, ['fields', 'nt_variants']);
    if (Array.isArray(variants)) {
      lists.push(variants);
    }
    Object.values(omit(value, ['sys', 'reference'])).forEach(visit);
  };
  visit(node);

  return lists;
};

const fetchVariants = async ({
  ids,
  locale,
  include,
}: {
  ids: string[];
  locale?: string;
  include: number;
}): Promise<Entry<unknown>[]> => {
  try {
    const { items } = await contentfulClient.getEntries<unknown>({
      'sys.id[in]': ids.join(','),
      locale,
      include,
      limit: ids.length,
    });
    return items;
  } catch (error) {
    console.warn('[contentful] Failed to load experience variants', ids, error);
    return [];
  }
};

export const loadExperienceVariants = async <T>({
  node,
  locale,
  include,
}: {
  node: T;
  locale?: string;
  include: number;
}): Promise<T> => {
  const loaded = new Map<string, Entry<unknown>>();
  let lists = findVariantLists(node);

  for (let depth = 0; depth < MAX_NESTING_DEPTH && lists.length > 0; depth++) {
    const ids = [...new Set(lists.flat().map(getEntryId))].filter(
      (id): id is string => !!id && !loaded.has(id)
    );
    const batches: string[][] = [];
    for (let offset = 0; offset < ids.length; offset += BATCH_SIZE) {
      batches.push(ids.slice(offset, offset + BATCH_SIZE));
    }
    const items = (
      await Promise.all(batches.map((batch) => fetchVariants({ ids: batch, locale, include })))
    ).flat();
    items.forEach((item) => loaded.set(item.sys.id, item));

    for (const list of lists) {
      list.forEach((variant, index) => {
        const id = getEntryId(variant);
        list[index] = (id && loaded.get(id)) || variant;
      });
    }
    lists = findVariantLists(items);
  }

  return node;
};
